import { useState, useMemo, useEffect, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const PUB_TOKEN = process.env.REACT_APP_LOGO_DEV_TOKEN;

// ─── Manual domain overrides (name → exact domain) ───────────────────────────
// Add entries here for companies where brand-search returns the wrong domain.
const DOMAIN_OVERRIDES = {
  "uber china":   "uber.com",
  "grabwheels":   "grab.com",
  "aurora":       "aurora.tech",
  "ainnovation":  "ainnovation.com",
  "pacific union": "puprime.com",
  "abogen biosciences": "abogenbio.com",
  "perplexity":   "perplexity.ai",
  "aleo":         "aleo.org",
};

// localStorage cache key for resolved domains — prevents repeat API calls
const DOMAIN_CACHE_PREFIX = "wokelo_logo_domain_";

function getCachedDomain(permalink) {
  try { return localStorage.getItem(DOMAIN_CACHE_PREFIX + permalink) || null; } catch { return null; }
}
function setCachedDomain(permalink, domain) {
  try { localStorage.setItem(DOMAIN_CACHE_PREFIX + permalink, domain); } catch {}
}

/**
 * CompanyLogo — shows a logo.dev image for a portfolio company.
 *
 * Resolution order:
 *   1. Domain confirmed via ConfirmCompanyModal (wokelo_cw_{permalink})
 *   2. Domain cached from a previous brand-search API call
 *   3. Backend /api/logo/search — searches by company name, tries fallbacks
 *   4. Initials colored avatar if no domain found or image errors
 *
 * The secret key never leaves the backend — only the publishable key is used in img URLs.
 */
export function CompanyLogo({ name = "", permalink = "", size = 20 }) {
  const [imgError, setImgError] = useState(false);
  const [domain, setDomain] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const resolve = async () => {
      // 0. Manual override — highest priority, bypasses all cache
      const override = DOMAIN_OVERRIDES[name.toLowerCase().trim()];
      if (override) { setDomain(override); return; }
      try {
        const confirmed = localStorage.getItem(`wokelo_cw_${permalink}`);
        if (confirmed) {
          const d = confirmed.replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim();
          if (d) { setDomain(d); return; }
        }
      } catch {}

      // 2. Previously cached brand-search result
      const cached = getCachedDomain(permalink);
      if (cached) { setDomain(cached); return; }

      // 3. Ask backend (which holds the secret key)
      if (!name) {
        // Last resort: permalink.com guess
        const guess = `${(permalink || "").toLowerCase().replace(/[^a-z0-9-]/g, "")}.com`;
        setDomain(guess); return;
      }
      try {
        const res = await fetch(`${API}/api/logo/search?name=${encodeURIComponent(name)}`);
        if (res.ok) {
          const { domain: found } = await res.json();
          if (found) {
            setCachedDomain(permalink, found);
            setDomain(found);
            return;
          }
        }
      } catch {}

      // 4. Fallback: permalink.com
      const fallback = `${(permalink || name).toLowerCase().replace(/[^a-z0-9-]/g, "")}.com`;
      setDomain(fallback);
    };

    resolve();
  }, [name, permalink]);

  // Initials + deterministic hue
  const initials = name.split(" ").slice(0, 2).map(w => (w[0] || "").toUpperCase()).join("");
  const hue = useMemo(
    () => [...(name || "")].reduce((acc, c) => acc + c.charCodeAt(0), 0) * 47 % 360,
    [name]
  );

  const avatarStyle = {
    width: size, height: size, borderRadius: "4px",
    background: `hsl(${hue}, 38%, 30%)`, color: "#fff",
    fontSize: Math.max(8, Math.round(size * 0.38)), fontWeight: 700,
    letterSpacing: "-0.02em", display: "inline-flex",
    alignItems: "center", justifyContent: "center",
    flexShrink: 0, fontFamily: "sans-serif", userSelect: "none",
  };

  if (!domain || !PUB_TOKEN || imgError) {
    return (
      <span
        style={avatarStyle}
        title={name}
        data-testid={`company-logo-${(permalink || name).replace(/\s+/g, "-")}`}
      >
        {initials || "?"}
      </span>
    );
  }

  return (
    <img
      src={`https://img.logo.dev/${domain}?token=${PUB_TOKEN}&size=${size * 2}&format=png`}
      alt={`${name} logo`}
      width={size}
      height={size}
      style={{
        borderRadius: "4px", objectFit: "contain", flexShrink: 0,
        background: "#FFFFFF", border: "1px solid #F0F2F4", display: "inline-block",
      }}
      onError={() => setImgError(true)}
      title={name}
      data-testid={`company-logo-${(permalink || name).replace(/\s+/g, "-")}`}
    />
  );
}

